import type { ReportDef, Filter, ReportResult } from "./reporting";
import { runReport } from "./reporting";
import type { Workspace } from "./types";

/* Persistence + execution layer for dashboards.
 *
 * When the Penumbrai API is reachable, dashboards are stored and reports are
 * computed server-side (Postgres-ready, scales past the browser). When it
 * isn't — a standalone demo — everything falls back to localStorage + the
 * in-browser engine, so the product still works end to end. The `mode` the UI
 * shows ("Live API" vs "Local demo") reflects which path is live. */

export interface Widget extends ReportDef { span: number }
export interface Dashboard {
  id: string;
  name: string;
  ownerId: string;
  ownerName: string;
  visibility: "private" | "shared";
  widgets: Widget[];
  updatedAt: string;
}
export interface SourceMeta { id: string; label: string; icon: string; supportsLine?: boolean; dims: { id: string; label: string }[]; measures: { id: string; label: string }[]; }
export type PersistMode = "api" | "local";

const API = (import.meta.env.VITE_API_URL as string | undefined) || "/api";
const LS_KEY = "penumbrai:dashboards";

async function req<T>(path: string, opts?: RequestInit, timeoutMs = 4000): Promise<T | null> {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    const res = await fetch(`${API}${path}`, { credentials: "include", headers: { "content-type": "application/json" }, signal: ctrl.signal, ...opts });
    clearTimeout(t);
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export const DEFAULT_WIDGETS: Widget[] = [
  { id: "w1", title: "ARR by segment", source: "accounts", viz: "bar", measureId: "arr", dimensionId: "segment", span: 3 },
  { id: "w2", title: "Accounts by health band", source: "accounts", viz: "donut", measureId: "count", dimensionId: "healthBand", span: 3 },
  { id: "w3", title: "Portfolio health — last 12 weeks", source: "accounts", viz: "line", measureId: "avgHealth", span: 6 },
  { id: "w4", title: "Signals by source", source: "signals", viz: "bar", measureId: "count", dimensionId: "source", span: 3 },
  { id: "w5", title: "Tasks by status", source: "tasks", viz: "donut", measureId: "count", dimensionId: "status", span: 3 },
  { id: "w6", title: "Records synced by category", source: "integrations", viz: "bar", measureId: "recordsSynced", dimensionId: "category", span: 3 },
  { id: "w7", title: "Avg uptime by region", source: "deployment", viz: "bar", measureId: "uptime_30d__avg", dimensionId: "region", span: 3 },
  { id: "w8", title: "Conversations by sentiment", source: "conversation", viz: "donut", measureId: "count", dimensionId: "sentiment", span: 3 },
  { id: "w9", title: "Playbook runs by category", source: "playbooks", viz: "bar", measureId: "activeRuns", dimensionId: "category", span: 3 },
  { id: "w10", title: "Top accounts by ARR", source: "accounts", viz: "table", measureId: "arr", span: 6, limit: 6 },
];

function readLocal(): Dashboard[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw) as Dashboard[];
  } catch { /* ignore */ }
  return [];
}
function writeLocal(ds: Dashboard[]) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(ds)); } catch { /* ignore */ }
}
function localSeed(me: { id: string; name: string }): Dashboard {
  return { id: "dash_overview", name: "Portfolio overview", ownerId: me.id, ownerName: me.name, visibility: "private", widgets: DEFAULT_WIDGETS, updatedAt: "2026-08-16" };
}

/** Load the dashboard list and decide the mode from API reachability. A reachable
 * API is authoritative even when it returns an empty list (a brand-new account
 * simply has no dashboards yet) — only an unreachable API falls back to local. */
export async function loadDashboards(me: { id: string; name: string }): Promise<{ mode: PersistMode; dashboards: Dashboard[] }> {
  const api = await req<Dashboard[]>("/dashboards");
  if (api) return { mode: "api", dashboards: api };
  const local = readLocal();
  return { mode: "local", dashboards: local.length ? local : [localSeed(me)] };
}

export async function createDashboard(mode: PersistMode, me: { id: string; name: string }, input: Pick<Dashboard, "name" | "visibility" | "widgets">): Promise<Dashboard> {
  if (mode === "api") {
    const d = await req<Dashboard>("/dashboards", { method: "POST", body: JSON.stringify(input) });
    if (d) return d;
  }
  const d: Dashboard = { id: `dash_${Date.now().toString(36)}`, ownerId: me.id, ownerName: me.name, updatedAt: "2026-08-16", ...input };
  writeLocal([...readLocal().filter((x) => x.id !== d.id), d]);
  return d;
}

export async function updateDashboard(mode: PersistMode, id: string, patch: Partial<Pick<Dashboard, "name" | "visibility" | "widgets">>): Promise<Dashboard | null> {
  if (mode === "api") {
    const d = await req<Dashboard>(`/dashboards/${id}`, { method: "PUT", body: JSON.stringify(patch) });
    if (d) return d;
  }
  const all = readLocal();
  const idx = all.findIndex((x) => x.id === id);
  if (idx < 0) return null;
  all[idx] = { ...all[idx], ...patch, updatedAt: "2026-08-16" };
  writeLocal(all);
  return all[idx];
}

export async function deleteDashboard(mode: PersistMode, id: string): Promise<void> {
  if (mode === "api") { await req(`/dashboards/${id}`, { method: "DELETE" }); return; }
  writeLocal(readLocal().filter((x) => x.id !== id));
}

/** Compute a batch of report defs — server-side when live, else in-browser. */
export async function computeResults(mode: PersistMode, ws: Workspace, defs: ReportDef[], filters: Filter[]): Promise<Record<string, ReportResult>> {
  if (mode === "api") {
    const res = await req<ReportResult[]>("/reports/run-batch", { method: "POST", body: JSON.stringify({ defs, filters }) });
    if (res && res.length === defs.length) return Object.fromEntries(defs.map((d, i) => [d.id, res[i]]));
  }
  return Object.fromEntries(defs.map((d) => [d.id, runReport(ws, d, filters)]));
}
